import React, { ImgHTMLAttributes, useCallback, useEffect, useState } from "react";
import { IMakeWesrvURL, makeWsrvURL } from "../../lib/Media/util";
import {
    LazyLoadImage,
    LazyLoadImageProps,
} from "react-lazy-load-image-component";

// Enhanced optimization options (limited by IMakeWesrvURL interface)
export interface IWsrvOptimizationOptions {
    quality?: number;
    background?: string;
    output?: 'jpg' | 'webp' | 'avif' | 'png';
}

export interface IWsrvImageProps {
    urlParams: IMakeWesrvURL;
    imgProps?: Partial<LazyLoadImageProps>;
    stops?: number[];
    sizeBuffer?: number;
    widthMax?: number;
    className?: string;
    lazy?: boolean;
    // Enhanced optimization props
    optimizationOptions?: IWsrvOptimizationOptions;
    priority?: 'high' | 'normal' | 'low';
    preload?: boolean;
    eagerLoad?: boolean; // Force eager loading regardless of lazy prop
}

export const WsrvImage: React.FC<IWsrvImageProps> = ({
    urlParams,
    imgProps,
    stops = [400, 600, 800, 1000, 1200, 1600, 2000, 2400],
    sizeBuffer = 200,
    widthMax = 2400,
    lazy = true,
    className,
    optimizationOptions = {
        quality: 85,
        background: 'white',
        output: 'webp'
    },
    priority = 'normal',
    preload = false,
    eagerLoad = false,
}) => {
    const [isPreloaded, setIsPreloaded] = useState(false);

    // Enhanced URL generation with optimization
    const getOptimizedUrl = useCallback((width?: number, isThumbnail = false) => {
        const enhancedParams = {
            ...urlParams,
            ...(width && { width }),
            // Apply optimization options - convert quality to string
            quality: String(isThumbnail ? 
                Math.min(optimizationOptions.quality || 85, 70) : 
                optimizationOptions.quality || 85),
            background: optimizationOptions.background || 'white',
            output: optimizationOptions.output || 'webp'
        };
        return makeWsrvURL(enhancedParams);
    }, [urlParams, optimizationOptions]);

    // Preloading logic
    useEffect(() => {
        if (preload && !isPreloaded) {
            const img = new Image();
            img.onload = () => setIsPreloaded(true);
            img.onerror = () => setIsPreloaded(true); // Prevent infinite retries
            
            // Use higher quality for preloading if high priority
            const preloadQuality = priority === 'high' ? 90 : 70;
            const preloadParams = {
                ...urlParams,
                width: Math.min(1280, widthMax),
                quality: String(preloadQuality),
                background: optimizationOptions.background || 'white',
                output: optimizationOptions.output || 'webp'
            };
            img.src = makeWsrvURL(preloadParams);
        }
    }, [preload, isPreloaded, urlParams, widthMax, priority, optimizationOptions]);

    // Generate optimized URLs for srcSet
    let srcSet: string[] = [];
    let sizes: string[] = [];
    let url = getOptimizedUrl();
    
    stops.map((w) => {
        if (w <= widthMax + sizeBuffer) {
            srcSet.push(getOptimizedUrl(w) + " " + w + "w");
            sizes.push("(max-width: " + (w - sizeBuffer) + "px) " + w + "px");
        }
    });

    // Determine loading strategy
    const shouldEagerLoad = eagerLoad || priority === 'high' || !lazy;
    const loadingValue = shouldEagerLoad ? "eager" : "lazy";

    let fullProps: Omit<
        ImgHTMLAttributes<HTMLImageElement>,
        "placeholder" | "onload"
    > = {
        src: url,
        srcSet: srcSet.join(","),
        sizes: sizes.join(","),
        className: className,
        loading: loadingValue,
        // Add fetchpriority for high priority images
        ...(priority === 'high' && { fetchpriority: "high" }),
        ...imgProps,
    };

    return shouldEagerLoad ? (
        <img {...fullProps} id="optimized-image" />
    ) : (
        <LazyLoadImage effect="opacity" {...fullProps} />
    );
};

export default WsrvImage
